<template>
  <div class="location-info" v-if="location">
    <headline bg>
      {{ $t("location.additional-information") }}
    </headline>

    <v-row>
      <!-- Colonne gauche : Informations -->
      <v-col>
        <v-row>
          <v-col class="label" sm="3">{{ $t('name') }} :</v-col>
          <v-col sm="9">
            {{ location.name }}
            <v-btn small class="ml-3" :to="{name: 'location.edit'}">
              {{ $t('charging-point.edit-location') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.organization') }} :</v-col>
          <v-col sm="9">
            <router-link :to="{name: 'organizations.detail', params: {id: location.organization.id }}" v-if="location.organization">
              {{ location.organization.name }}
            </router-link>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label" sm="3">{{ $tc('pdl.pdl') }} :</v-col>
          <v-col>
            <template v-if="location.pdlId">
              {{ location.pdlName || $tc('pdl.pdl') }}
              <template v-if="location.pdlReference"> · {{ location.pdlReference }}</template>
              <v-chip v-if="location.pdlIsDefault" x-small class="ml-1">{{ $t('pdl.default') }}</v-chip>
              <span v-if="location.pdlOrganizationName && location.organization && location.pdlOrganizationId !== location.organization.id" class="text--secondary">
                — {{ $t('pdl.fed-by', {organization: location.pdlOrganizationName}) }}
              </span>
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <v-row>
          <v-col class="label" sm="3">{{ $t('address') }} :</v-col>
          <v-col sm="9">
            <template v-if="location.address && location.address.street">
              {{ location.address.number }} {{ location.address.street }}
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label" sm="3">{{ $t('additional-information') }} :</v-col>
          <v-col sm="9">
            <template v-if="location.address && location.address.additionalInformation">
              {{ location.address.additionalInformation }}
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('zipcode') }} :</v-col>
          <v-col>{{ location.address?.zipcode || '-' }}</v-col>
          <v-col class="label">{{ $t('city') }} :</v-col>
          <v-col>{{ location.address?.city || '-' }}</v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('state') }} :</v-col>
          <v-col>{{ location.address?.state || '-' }}</v-col>
          <v-col class="label">{{ $t('country') }} :</v-col>
          <v-col>{{ location.address?.country || '-' }}</v-col>
        </v-row>

        <v-divider class="my-6" />

        <v-row>
          <v-col>
            <div class="switch-row">
              <span class="label">{{ $t('publish') }} :</span>
              <v-switch
                  color="#00897E"
                  inset
                  hide-details
                  readonly
                  v-model="location.publish"
              />
            </div>
          </v-col>
          <v-col>
            <div class="switch-row">
              <span class="label">{{ $t('private') }} :</span>
              <v-switch
                  color="#00897E"
                  inset
                  hide-details
                  readonly
                  v-model="location.private"
              />
            </div>
          </v-col>
          <v-col>
            <div class="switch-row">
              <span class="label">{{ $t('En maintenance') }} :</span>
              <v-switch
                  color="#00897E"
                  inset
                  hide-details
                  readonly
                  v-model="location.inMaintenance"
              />
            </div>
          </v-col>
        </v-row>


        <v-divider class="my-6" />

        <v-row>
          <v-col class="label">{{ $t('location-type') }} :</v-col>
          <v-col>
            <template v-if="location.type">
              {{ $t('enums.location-type.' + location.type) }}
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('location.owner-types') }} :</v-col>
          <v-col>
            <template v-if="location.ownerType">
              {{ $t('enums.location-owner-type.' + location.ownerType) }}
            </template>
            <template v-else>-</template>
          </v-col>
          <v-col class="label">{{ $t('accessibility') }} :</v-col>
          <v-col class="primary--text">
            <template v-if="location.accessibility">
              {{ $t('enums.location-accessibility.' + location.accessibility) }}
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('location.timezone') }} :</v-col>
          <v-col>{{ location.timezone || '-' }}</v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('location.additional-information') }} :</v-col>
          <v-col>
            <template v-if="location.direction && location.direction.length > 0">
              <div v-for="(direction, index) in location.direction" :key="index">
                <template v-if="direction">{{ direction.value }}</template>
              </div>
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('facilities') }} :</v-col>
          <v-col>
            <template v-if="location.facilities && location.facilities.length > 0">
              <v-chip
                  v-for="(facility, index) in location.facilities"
                  :key="index"
                  small
                  class="mr-1 mb-1"
              >
                {{ $t('enums.location-facility.' + facility) }}
              </v-chip>
            </template>
            <template v-else>-</template>
          </v-col>
        </v-row>
      </v-col>

      <!-- Colonne droite : Carte et horaires -->
      <v-col>
        <v-row>
          <v-col>
            <div class="map-block">
              <Map ref="map" :items="markers" :isLocation="true"></Map>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="label">{{ $t('latitude') }} :</v-col>
          <v-col>{{ location.coordinates?.latitude || '-' }}</v-col>
          <v-col class="label">{{ $t('longitude') }} :</v-col>
          <v-col>{{ location.coordinates?.longitude || '-' }}</v-col>
        </v-row>

        <v-divider class="my-6" />

        <v-row>
          <v-col>
            <div class="switch-row">
              <span class="label">{{ $t('twenty-four-seven') }} :</span>
              <v-switch
                  color="#00897E"
                  inset
                  hide-details
                  readonly
                  v-model="location.twentyFourSeven"
              />
            </div>
          </v-col>
          <v-col>
            <div class="switch-row">
              <span class="label">{{ $t('location.charging-when-closed') }} :</span>
              <v-switch
                  color="#00897E"
                  inset
                  hide-details
                  readonly
                  v-model="location.chargingWhenClosed"
              />
            </div>
          </v-col>
        </v-row>

        <v-row v-if="location.openingTimes && location.openingTimes.length > 0">
          <v-col>
            <div class="label mb-2">{{ $t('opening-hours') }}</div>
            <v-list dense>
              <v-list-item
                  v-for="(item, i) in location.openingTimes"
                  :key="i"
                  class="px-0"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-clock</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('businessHour.days.' + item.weekday.toLowerCase()) }}
                    : {{ item.periodBegin }} - {{ item.periodEnd }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Map from '@blocks/Map'

export default {
  components: {
    Map,
  },
  data() {
    return {
      markers: [],
    }
  },
  mounted() {
    this.loadMarkers()
  },

  watch: {
    location: {
      handler() {
        this.loadMarkers()
      },
      deep: true
    }
  },

  computed: {
    location() {
      return this.$parent.location
    },
  },
  methods: {
    loadMarkers() {
      this.markers = [];
      if (this.location?.coordinates) {
        this.markers.push({
          id: this.location.id,
          position: {
            latitude: this.location.coordinates.latitude,
            longitude: this.location.coordinates.longitude,
          },
          status: [{
            0: 'AVAILABLE'
          }]
        })
      }
    },
  },
}
</script>

<style lang="scss">
.location-info {
  .label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
  }

  .switch-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .v-input--switch {
      margin: 0;
      padding: 0;
    }
  }

  .map-block {
    border: 3px solid #E53C22;
    border-radius: 0 10px 10px 0;
    overflow: hidden;
    height: 400px;
  }
}
</style>
